export type MapViewState = { longitude: number; latitude: number; zoom: number };

/**
 * mapbox-gl 1.x does not notice container size changes (react-map-gl only wires
 * a container observer for maplibre). Calls `onResize` whenever the element's
 * size changes, throttled to one call per animation frame. Returns a disposer.
 * A no-op where ResizeObserver does not exist (jsdom, very old browsers).
 */
export function observeElementResize(
  element: Element | null,
  onResize: () => void,
): () => void {
  if (!element || typeof ResizeObserver === "undefined") {
    return () => {};
  }
  let frame: number | null = null;
  const observer = new ResizeObserver(() => {
    if (frame !== null) {
      return;
    }
    frame = requestAnimationFrame(() => {
      frame = null;
      onResize();
    });
  });
  observer.observe(element);
  return () => {
    observer.disconnect();
    if (frame !== null) {
      cancelAnimationFrame(frame);
      frame = null;
    }
  };
}

/**
 * react-map-gl ignores `initialViewState` changes after creation. Returns the
 * view to jump to when the primary building differs from the one the map was
 * initialised (or last centred) with, otherwise null.
 */
export function recentreTarget(
  centredOnId: string | undefined,
  primaryId: string | undefined,
  viewState: MapViewState,
): { center: [number, number]; zoom: number } | null {
  if (!primaryId || primaryId === centredOnId) {
    return null;
  }
  return { center: [viewState.longitude, viewState.latitude], zoom: viewState.zoom };
}
